import { useRef, useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Play, 
  Pause, 
  SkipBack, 
  SkipForward, 
  Volume2, 
  VolumeX,
  Maximize,
  Minimize,
  Target,
  X,
  Car,
  Gauge,
  Activity
} from 'lucide-react';
import { Detection, Vehicle } from '../types';
import { formatDuration, getSpeedColor } from '../utils/helpers';

interface VideoPlayerProps {
  file: File | null;
  processedVideoUrl?: string;
  detections?: Detection[];
  vehicles?: Vehicle[];
  currentFrame?: number;
  onFrameChange?: (frame: number) => void;
  onVehicleSelect?: (vehicle: Vehicle | null) => void;
}

export function VideoPlayer({ 
  file, 
  processedVideoUrl, 
  detections = [],
  vehicles = [],
  onFrameChange,
  onVehicleSelect
}: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [currentDetections, setCurrentDetections] = useState<Detection[]>([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | null>(null);
  const [hoveredVehicleId, setHoveredVehicleId] = useState<string | null>(null);

  // Build video source URL - handle relative paths from backend
  const getVideoSrc = () => {
    if (processedVideoUrl) {
      // If it's a relative URL from backend, prepend the API base URL
      if (processedVideoUrl.startsWith('/')) {
        const apiBase = import.meta.env.VITE_API_URL || 'http://localhost:8000';
        return `${apiBase}${processedVideoUrl}`;
      }
      return processedVideoUrl;
    }
    if (file) {
      return URL.createObjectURL(file);
    }
    return null;
  };
  
  const videoSrc = getVideoSrc();

  // Get selected vehicle data
  const selectedVehicle = vehicles.find(v => v.id === selectedVehicleId);
  const selectedDetection = currentDetections.find(d => d.vehicle_id === selectedVehicleId);

  // Update current detections based on frame
  useEffect(() => {
    if (!videoRef.current || detections.length === 0) return;

    const fps = 30;
    const currentFrame = Math.floor(currentTime * fps);
    
    const frameDetections = detections.filter(
      d => Math.abs(d.frame - currentFrame) <= 2
    );
    
    setCurrentDetections(frameDetections);
    onFrameChange?.(currentFrame);
  }, [currentTime, detections, onFrameChange]);

  // Get vehicle number from ID
  const getVehicleNumber = (vehicleId: string): number => {
    try {
      return parseInt(vehicleId.split('_')[1]) || 0;
    } catch {
      return 0;
    }
  };

  // Get color for vehicle
  const getVehicleColor = (detection: Detection): string => {
    if (detection.color) {
      return `rgb(${detection.color[0]}, ${detection.color[1]}, ${detection.color[2]})`;
    }
    const colors = [
      '#0000FF', '#00FF00', '#FF0000', '#00FFFF', '#FF00FF', '#FFFF00',
      '#FF8000', '#8000FF', '#00FF80', '#FF0080', '#80FF00', '#0080FF'
    ];
    const num = getVehicleNumber(detection.vehicle_id);
    return colors[num % colors.length];
  };

  // Handle canvas click to select vehicle
  const handleCanvasClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video) return;

    const rect = canvas.getBoundingClientRect();
    const clickX = (e.clientX - rect.left) * (canvas.width / rect.width);
    const clickY = (e.clientY - rect.top) * (canvas.height / rect.height);

    const scaleX = canvas.width / (video.videoWidth || 1);
    const scaleY = canvas.height / (video.videoHeight || 1);

    // Check if click is inside any detection box
    for (const detection of currentDetections) {
      const [x, y, w, h] = detection.bbox;
      const scaledX = x * scaleX;
      const scaledY = y * scaleY;
      const scaledW = w * scaleX;
      const scaledH = h * scaleY;

      if (
        clickX >= scaledX && clickX <= scaledX + scaledW &&
        clickY >= scaledY && clickY <= scaledY + scaledH
      ) {
        const newSelectedId = detection.vehicle_id === selectedVehicleId ? null : detection.vehicle_id;
        setSelectedVehicleId(newSelectedId);
        
        const vehicle = vehicles.find(v => v.id === newSelectedId);
        onVehicleSelect?.(vehicle || null);
        return;
      }
    }

    // Click outside any vehicle - deselect
    setSelectedVehicleId(null);
    onVehicleSelect?.(null);
  }, [currentDetections, selectedVehicleId, vehicles, onVehicleSelect]);

  // Handle mouse move for hover effect
  const handleCanvasMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video) return;

    const rect = canvas.getBoundingClientRect();
    const mouseX = (e.clientX - rect.left) * (canvas.width / rect.width);
    const mouseY = (e.clientY - rect.top) * (canvas.height / rect.height);

    const scaleX = canvas.width / (video.videoWidth || 1);
    const scaleY = canvas.height / (video.videoHeight || 1);

    let foundHover = false;
    for (const detection of currentDetections) {
      const [x, y, w, h] = detection.bbox;
      const scaledX = x * scaleX;
      const scaledY = y * scaleY;
      const scaledW = w * scaleX;
      const scaledH = h * scaleY;

      if (
        mouseX >= scaledX && mouseX <= scaledX + scaledW &&
        mouseY >= scaledY && mouseY <= scaledY + scaledH
      ) {
        setHoveredVehicleId(detection.vehicle_id);
        foundHover = true;
        break;
      }
    }

    if (!foundHover) {
      setHoveredVehicleId(null);
    }
  }, [currentDetections]);

  // Draw detection boxes on canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    
    if (!canvas || !video) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = video.videoWidth || video.clientWidth;
    canvas.height = video.videoHeight || video.clientHeight;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (currentDetections.length === 0) return;

    const scaleX = canvas.width / (video.videoWidth || 1);
    const scaleY = canvas.height / (video.videoHeight || 1);

    // Draw non-selected vehicles first (dimmed if there's a selection)
    currentDetections.forEach((detection) => {
      const isSelected = detection.vehicle_id === selectedVehicleId;
      const isHovered = detection.vehicle_id === hoveredVehicleId;
      
      if (isSelected) return; // Draw selected last

      const [x, y, w, h] = detection.bbox;
      const vehicleColor = getVehicleColor(detection);
      const speedColor = getSpeedColor(detection.speed);
      const vehicleNum = getVehicleNumber(detection.vehicle_id);

      const scaledX = x * scaleX;
      const scaledY = y * scaleY;
      const scaledW = w * scaleX;
      const scaledH = h * scaleY;

      // Dim non-selected vehicles if there's a selection
      const alpha = selectedVehicleId && !isHovered ? 0.4 : 1;
      ctx.globalAlpha = alpha;

      // Draw bounding box
      ctx.strokeStyle = vehicleColor;
      ctx.lineWidth = isHovered ? 4 : 2;
      ctx.strokeRect(scaledX, scaledY, scaledW, scaledH);

      // Draw vehicle number badge
      const badgeRadius = 16;
      const badgeCenterX = scaledX + scaledW / 2;
      const badgeCenterY = scaledY - badgeRadius - 6;

      ctx.beginPath();
      ctx.arc(badgeCenterX, badgeCenterY, badgeRadius, 0, Math.PI * 2);
      ctx.fillStyle = vehicleColor;
      ctx.fill();

      ctx.fillStyle = '#fff';
      ctx.font = 'bold 14px JetBrains Mono';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(String(vehicleNum), badgeCenterX, badgeCenterY);

      // Speed label
      const speedLabel = `${detection.speed.toFixed(0)} km/h`;
      ctx.font = 'bold 12px JetBrains Mono';
      const speedLabelWidth = ctx.measureText(speedLabel).width + 10;
      const speedLabelX = scaledX + scaledW / 2 - speedLabelWidth / 2;
      const speedLabelY = scaledY + scaledH + 6;

      ctx.fillStyle = speedColor;
      ctx.beginPath();
      ctx.roundRect(speedLabelX, speedLabelY, speedLabelWidth, 18, 3);
      ctx.fill();

      ctx.fillStyle = '#000';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillText(speedLabel, scaledX + scaledW / 2, speedLabelY + 3);

      // Hover hint
      if (isHovered && !selectedVehicleId) {
        ctx.fillStyle = 'rgba(255,255,255,0.9)';
        ctx.font = '11px JetBrains Mono';
        ctx.fillText('Click to track', scaledX + scaledW / 2, scaledY - badgeRadius * 2 - 12);
      }

      ctx.globalAlpha = 1;
    });

    // Draw selected vehicle on top with highlight
    if (selectedVehicleId) {
      const detection = currentDetections.find(d => d.vehicle_id === selectedVehicleId);
      if (detection) {
        const [x, y, w, h] = detection.bbox;
        const vehicleColor = getVehicleColor(detection);
        const speedColor = getSpeedColor(detection.speed);
        const vehicleNum = getVehicleNumber(detection.vehicle_id);

        const scaledX = x * scaleX;
        const scaledY = y * scaleY;
        const scaledW = w * scaleX;
        const scaledH = h * scaleY;

        // Glowing effect
        ctx.shadowColor = vehicleColor;
        ctx.shadowBlur = 20;

        // Thick border
        ctx.strokeStyle = vehicleColor;
        ctx.lineWidth = 4;
        ctx.strokeRect(scaledX, scaledY, scaledW, scaledH);

        // Corner brackets
        const cornerLen = Math.min(scaledW * 0.25, scaledH * 0.25, 25);
        ctx.lineWidth = 6;
        
        // Top-left
        ctx.beginPath();
        ctx.moveTo(scaledX, scaledY + cornerLen);
        ctx.lineTo(scaledX, scaledY);
        ctx.lineTo(scaledX + cornerLen, scaledY);
        ctx.stroke();
        
        // Top-right
        ctx.beginPath();
        ctx.moveTo(scaledX + scaledW - cornerLen, scaledY);
        ctx.lineTo(scaledX + scaledW, scaledY);
        ctx.lineTo(scaledX + scaledW, scaledY + cornerLen);
        ctx.stroke();
        
        // Bottom-left
        ctx.beginPath();
        ctx.moveTo(scaledX, scaledY + scaledH - cornerLen);
        ctx.lineTo(scaledX, scaledY + scaledH);
        ctx.lineTo(scaledX + cornerLen, scaledY + scaledH);
        ctx.stroke();
        
        // Bottom-right
        ctx.beginPath();
        ctx.moveTo(scaledX + scaledW - cornerLen, scaledY + scaledH);
        ctx.lineTo(scaledX + scaledW, scaledY + scaledH);
        ctx.lineTo(scaledX + scaledW, scaledY + scaledH - cornerLen);
        ctx.stroke();

        ctx.shadowBlur = 0;

        // Large badge with "TRACKING" indicator
        const badgeRadius = 22;
        const badgeCenterX = scaledX + scaledW / 2;
        const badgeCenterY = scaledY - badgeRadius - 10;

        // Outer ring animation effect
        ctx.beginPath();
        ctx.arc(badgeCenterX, badgeCenterY, badgeRadius + 4, 0, Math.PI * 2);
        ctx.strokeStyle = vehicleColor;
        ctx.lineWidth = 2;
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(badgeCenterX, badgeCenterY, badgeRadius, 0, Math.PI * 2);
        ctx.fillStyle = vehicleColor;
        ctx.fill();

        ctx.fillStyle = '#fff';
        ctx.font = 'bold 16px JetBrains Mono';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(String(vehicleNum), badgeCenterX, badgeCenterY);

        // "TRACKING" label
        ctx.fillStyle = vehicleColor;
        ctx.font = 'bold 10px JetBrains Mono';
        ctx.fillText('● TRACKING', badgeCenterX, badgeCenterY - badgeRadius - 10);

        // Speed label (larger)
        const speedLabel = `${detection.speed.toFixed(0)} km/h`;
        ctx.font = 'bold 14px JetBrains Mono';
        const speedLabelWidth = ctx.measureText(speedLabel).width + 14;
        const speedLabelX = scaledX + scaledW / 2 - speedLabelWidth / 2;
        const speedLabelY = scaledY + scaledH + 8;

        ctx.fillStyle = speedColor;
        ctx.beginPath();
        ctx.roundRect(speedLabelX, speedLabelY, speedLabelWidth, 22, 4);
        ctx.fill();

        ctx.fillStyle = '#000';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        ctx.fillText(speedLabel, scaledX + scaledW / 2, speedLabelY + 4);

        // Vehicle type
        if (detection.vehicle_type) {
          ctx.font = 'bold 11px JetBrains Mono';
          ctx.fillStyle = '#fff';
          ctx.textAlign = 'left';
          ctx.fillText(detection.vehicle_type.toUpperCase(), scaledX + 6, scaledY + scaledH - 8);
        }
      }
    }

    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
  }, [currentDetections, selectedVehicleId, hoveredVehicleId]);

  const togglePlay = useCallback(() => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  }, [isPlaying]);

  const handleSeek = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (!videoRef.current) return;
    const time = parseFloat(e.target.value);
    videoRef.current.currentTime = time;
    setCurrentTime(time);
  }, []);

  const handleVolumeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (!videoRef.current) return;
    const vol = parseFloat(e.target.value);
    videoRef.current.volume = vol;
    setVolume(vol);
    setIsMuted(vol === 0);
  }, []);

  const toggleMute = useCallback(() => {
    if (!videoRef.current) return;
    const newMuted = !isMuted;
    videoRef.current.muted = newMuted;
    setIsMuted(newMuted);
  }, [isMuted]);

  const toggleFullscreen = useCallback(() => {
    if (!containerRef.current) return;
    if (!isFullscreen) {
      containerRef.current.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
    setIsFullscreen(!isFullscreen);
  }, [isFullscreen]);

  const skip = useCallback((seconds: number) => {
    if (!videoRef.current) return;
    videoRef.current.currentTime = Math.max(
      0, 
      Math.min(duration, videoRef.current.currentTime + seconds)
    );
  }, [duration]);

  const clearSelection = useCallback(() => {
    setSelectedVehicleId(null);
    onVehicleSelect?.(null);
  }, [onVehicleSelect]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => setCurrentTime(video.currentTime);
    const handleDurationChange = () => setDuration(video.duration);
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => setIsPlaying(false);

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('durationchange', handleDurationChange);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('durationchange', handleDurationChange);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
    };
  }, []);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    
    const handleMouseMove = () => {
      setShowControls(true);
      clearTimeout(timeout);
      timeout = setTimeout(() => setShowControls(false), 3000);
    };

    const container = containerRef.current;
    container?.addEventListener('mousemove', handleMouseMove);

    return () => {
      container?.removeEventListener('mousemove', handleMouseMove);
      clearTimeout(timeout);
    };
  }, []);

  if (!videoSrc) {
    return (
      <div className="w-full aspect-video bg-dark-900 rounded-2xl flex items-center justify-center">
        <p className="text-dark-500">No video loaded</p>
      </div>
    );
  }

  return (
    <div className="relative">
      <div
        ref={containerRef}
        className="relative w-full bg-dark-950 rounded-2xl overflow-hidden group"
      >
        {/* Video element */}
        <video
          ref={videoRef}
          src={videoSrc}
          className="w-full aspect-video object-contain bg-black"
          onClick={togglePlay}
        >
          <track kind="captions" />
        </video>

        {/* Detection overlay canvas - now clickable */}
        {detections.length > 0 && (
          <canvas
            ref={canvasRef}
            className={`absolute inset-0 w-full h-full ${hoveredVehicleId ? 'cursor-pointer' : 'cursor-default'}`}
            onClick={handleCanvasClick}
            onMouseMove={handleCanvasMouseMove}
            onMouseLeave={() => setHoveredVehicleId(null)}
          />
        )}

        {/* Play/Pause overlay */}
        <motion.div
          initial={false}
          animate={{ opacity: showControls || !isPlaying ? 1 : 0 }}
          className="absolute inset-0 flex items-center justify-center bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none"
        >
          {!isPlaying && !selectedVehicleId && (
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={togglePlay}
              className="p-6 bg-primary-500/80 rounded-full pointer-events-auto"
            >
              <Play className="w-12 h-12 text-white fill-white" />
            </motion.button>
          )}
        </motion.div>

        {/* Tracking instruction hint */}
        {detections.length > 0 && !selectedVehicleId && currentDetections.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="absolute top-4 right-4 px-3 py-2 bg-dark-900/90 rounded-lg text-sm text-dark-300 flex items-center gap-2"
          >
            <Target className="w-4 h-4 text-primary-400" />
            Click a vehicle to track
          </motion.div>
        )}

        {/* Controls bar */}
        <motion.div
          initial={false}
          animate={{ opacity: showControls ? 1 : 0, y: showControls ? 0 : 20 }}
          className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/90 to-transparent"
        >
          <div className="mb-3">
            <input
              type="range"
              min={0}
              max={duration || 100}
              value={currentTime}
              onChange={handleSeek}
              className="w-full h-1 bg-dark-600 rounded-lg appearance-none cursor-pointer
                       [&::-webkit-slider-thumb]:appearance-none
                       [&::-webkit-slider-thumb]:w-3
                       [&::-webkit-slider-thumb]:h-3
                       [&::-webkit-slider-thumb]:bg-primary-500
                       [&::-webkit-slider-thumb]:rounded-full
                       [&::-webkit-slider-thumb]:cursor-pointer
                       [&::-webkit-slider-thumb]:shadow-lg"
              style={{
                background: `linear-gradient(to right, #6366f1 ${(currentTime / duration) * 100}%, #334155 ${(currentTime / duration) * 100}%)`
              }}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={() => skip(-10)}
                className="p-2 text-white/80 hover:text-white transition-colors"
                title="Skip back 10s"
              >
                <SkipBack className="w-5 h-5" />
              </button>

              <button
                onClick={togglePlay}
                className="p-2 text-white/80 hover:text-white transition-colors"
              >
                {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
              </button>

              <button
                onClick={() => skip(10)}
                className="p-2 text-white/80 hover:text-white transition-colors"
                title="Skip forward 10s"
              >
                <SkipForward className="w-5 h-5" />
              </button>

              <span className="ml-2 text-sm text-white/80 font-mono">
                {formatDuration(currentTime)} / {formatDuration(duration)}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 group/volume">
                <button
                  onClick={toggleMute}
                  className="p-2 text-white/80 hover:text-white transition-colors"
                >
                  {isMuted || volume === 0 ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                </button>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.1}
                  value={isMuted ? 0 : volume}
                  onChange={handleVolumeChange}
                  className="w-20 h-1 bg-dark-600 rounded-lg appearance-none cursor-pointer opacity-0 group-hover/volume:opacity-100 transition-opacity
                           [&::-webkit-slider-thumb]:appearance-none
                           [&::-webkit-slider-thumb]:w-3
                           [&::-webkit-slider-thumb]:h-3
                           [&::-webkit-slider-thumb]:bg-white
                           [&::-webkit-slider-thumb]:rounded-full"
                />
              </div>

              <button
                onClick={toggleFullscreen}
                className="p-2 text-white/80 hover:text-white transition-colors"
              >
                {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </motion.div>

        {/* Detection count badge */}
        {currentDetections.length > 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="absolute top-4 left-4 px-3 py-1.5 bg-primary-500/90 rounded-full text-white text-sm font-medium"
          >
            {currentDetections.length} vehicle{currentDetections.length !== 1 ? 's' : ''} in frame
          </motion.div>
        )}
      </div>

      {/* Selected vehicle info panel */}
      <AnimatePresence>
        {selectedVehicle && (
          <motion.div
            initial={{ opacity: 0, y: 20, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: 20, height: 0 }}
            className="mt-4 glass rounded-xl overflow-hidden"
          >
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                    style={{ backgroundColor: selectedVehicle.color ? `rgb(${selectedVehicle.color.join(',')})` : '#6366f1' }}
                  >
                    {getVehicleNumber(selectedVehicle.id)}
                  </div>
                  <div>
                    <h4 className="font-semibold text-dark-100 flex items-center gap-2">
                      <Target className="w-4 h-4 text-green-400" />
                      Tracking Vehicle #{getVehicleNumber(selectedVehicle.id)}
                    </h4>
                    <p className="text-sm text-dark-400 capitalize">{selectedVehicle.vehicle_type || 'Vehicle'}</p>
                  </div>
                </div>
                <button
                  onClick={clearSelection}
                  className="p-2 text-dark-400 hover:text-dark-200 hover:bg-dark-700 rounded-lg transition-colors"
                  title="Stop tracking"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-dark-800/50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-dark-400 text-xs mb-1">
                    <Gauge className="w-3 h-3" />
                    Current Speed
                  </div>
                  <p className="text-xl font-bold font-mono" style={{ color: selectedDetection ? getSpeedColor(selectedDetection.speed) : '#fff' }}>
                    {selectedDetection ? `${selectedDetection.speed.toFixed(0)} km/h` : '—'}
                  </p>
                </div>

                <div className="bg-dark-800/50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-dark-400 text-xs mb-1">
                    <Activity className="w-3 h-3" />
                    Avg Speed
                  </div>
                  <p className="text-xl font-bold font-mono text-primary-400">
                    {selectedVehicle.avg_speed.toFixed(0)} km/h
                  </p>
                </div>

                <div className="bg-dark-800/50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-dark-400 text-xs mb-1">
                    <Car className="w-3 h-3" />
                    Max Speed
                  </div>
                  <p className="text-xl font-bold font-mono text-red-400">
                    {selectedVehicle.max_speed.toFixed(0)} km/h
                  </p>
                </div>

                <div className="bg-dark-800/50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-dark-400 text-xs mb-1">
                    <Target className="w-3 h-3" />
                    Detections
                  </div>
                  <p className="text-xl font-bold font-mono text-dark-200">
                    {selectedVehicle.detections_count}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default VideoPlayer;
